/**
 * One of the Gaffer's posts drawn as a picture, so a shared update arrives in WhatsApp looking
 * like the tile in the Boot Room rather than as a bare link (lmsbe backlog item 92).
 *
 * Drawn on a canvas from what is already on the screen: the text comes from the post, and the
 * two pictures (his head, and the post's own picture when it has one) are the `<img>` elements
 * the tile is already showing. Nothing is downloaded, so this needs no request of its own - which
 * is why it does not go near `api/client.js`, and why an image that has not finished loading is
 * simply left out rather than waited for.
 *
 * Returns null wherever a canvas cannot be had (old browsers, and jsdom in tests). The caller
 * then shares the text alone, exactly as before.
 */

const WIDTH = 1080;
const PAD = 64;
const INNER = WIDTH - PAD * 2;
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

// The tile's own colours: a white card with the purple tint and bar of `.board-post.gaffer`.
const COLOURS = {
  page: '#ffffff',
  tint: 'rgba(168, 85, 247, 0.10)',
  bar: '#a855f7',
  name: '#7c3aed',
  text: '#1f1b2e',
  muted: '#6b6480',
  rule: 'rgba(168, 85, 247, 0.25)',
  out: '#d6164a',
};

/** Split text into lines no wider than `width` at the context's current font. */
export function wrap(ctx, text, width) {
  const lines = [];
  String(text).split('\n').forEach((paragraph) => {
    let line = '';
    paragraph.split(/\s+/).filter(Boolean).forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(candidate).width > width) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    });
    lines.push(line);
  });
  return lines;
}

const usable = (img) => Boolean(img && img.complete && img.naturalWidth > 0);

/**
 * Lay the tile out as a list of steps, each knowing its own height, so the canvas can be sized
 * before anything is drawn - resizing a canvas clears it.
 */
function layout(ctx, { name, body, facts, breakdown, picture }) {
  const steps = [];
  const add = (height, draw) => steps.push({ height, draw });

  add(140, (c, y) => {
    c.fillStyle = COLOURS.name;
    c.font = `700 44px ${FONT}`;
    c.fillText('The Gaffer', PAD + 128, y + 58);
    if (name) {
      c.fillStyle = COLOURS.muted;
      c.font = `400 32px ${FONT}`;
      c.fillText(name, PAD + 128, y + 104);
    }
  });

  if (facts) {
    Object.entries(facts).forEach(([label, value]) => add(60, (c, y) => {
      c.font = `400 34px ${FONT}`;
      c.fillStyle = COLOURS.muted;
      c.fillText(label, PAD, y + 40);
      c.fillStyle = COLOURS.text;
      c.font = `700 34px ${FONT}`;
      const text = String(value);
      c.fillText(text, WIDTH - PAD - c.measureText(text).width, y + 40);
    }));
    add(24, () => {});
  }

  if (breakdown) {
    breakdown.forEach((row) => add(60, (c, y) => {
      c.font = `600 34px ${FONT}`;
      c.fillStyle = COLOURS.text;
      c.fillText(row.team, PAD, y + 40);
      const figures = `${row.picks} backed · ${row.out} out`;
      c.font = `400 32px ${FONT}`;
      c.fillStyle = row.out > 0 ? COLOURS.out : COLOURS.muted;
      c.fillText(figures, WIDTH - PAD - c.measureText(figures).width, y + 40);
    }));
    add(24, () => {});
  }

  if (picture) {
    const scale = Math.min(INNER / picture.naturalWidth, 640 / picture.naturalHeight);
    const w = Math.round(picture.naturalWidth * scale);
    const h = Math.round(picture.naturalHeight * scale);
    add(h + 32, (c, y) => c.drawImage(picture, PAD + (INNER - w) / 2, y, w, h));
  }

  if (body) {
    ctx.font = `italic 500 42px ${FONT}`;
    wrap(ctx, body, INNER).forEach((line) => add(58, (c, y) => {
      c.font = `italic 500 42px ${FONT}`;
      c.fillStyle = COLOURS.text;
      c.fillText(line, PAD, y + 44);
    }));
  }

  add(96, (c, y) => {
    c.fillStyle = COLOURS.rule;
    c.fillRect(PAD, y + 30, INNER, 2);
    c.fillStyle = COLOURS.name;
    c.font = `700 30px ${FONT}`;
    c.fillText('gafferpicks.com', PAD, y + 80);
  });

  return steps;
}

/**
 * Draw the tile and hand back a PNG `File`, or null.
 *
 * @param {object} post   `{ name, body, facts, breakdown }` - what the tile shows
 * @param {object} images `{ head, picture }` - the tile's own `<img>` elements, either may be absent
 */
export async function drawPostImage(post, { head, picture } = {}) {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  let ctx = null;
  try {
    ctx = canvas.getContext('2d');
  } catch {
    ctx = null;
  }
  if (!ctx || typeof canvas.toBlob !== 'function') return null;

  const steps = layout(ctx, { ...post, picture: usable(picture) ? picture : null });
  canvas.width = WIDTH;
  canvas.height = PAD * 2 + steps.reduce((sum, s) => sum + s.height, 0);

  ctx.fillStyle = COLOURS.page;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = COLOURS.tint;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = COLOURS.bar;
  ctx.fillRect(0, 0, 14, canvas.height);

  if (usable(head)) ctx.drawImage(head, PAD, PAD + 16, 104, 104);

  let y = PAD;
  steps.forEach((step) => {
    step.draw(ctx, y);
    y += step.height;
  });

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) return null;
  return new File([blob], 'gaffer-update.png', { type: 'image/png' });
}
